import React from "react";
import { Input } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { NumericFormat } from "react-number-format";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import "flatpickr/dist/flatpickr.min.css";
import styled from "styled-components";
import classNames from "classnames";

const StyledInput = styled(Input)`
  height: 50px;
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.black100};
  border-radius: 13px;
  padding: 2px 10px;
  padding-bottom: 4px;
  padding-top: 2px;

  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colors.systemBlue};
  }
`;

const StyleNumericFormat = styled(NumericFormat)`
  height: 50px;
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.black100};
  border-radius: 13px;
  padding: 2px 10px;
  padding-bottom: 4px;
  padding-top: 2px;
  width: 100%;

  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colors.systemBlue};
    outline: none;
  }
`;

const StyledTextarea = styled(Input)`
  border: 2px solid ${({ theme }) => theme.colors.black100};
  background: transparent;
  border-radius: 13px;

  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colors.systemBlue};
  }
`;

const StyledDatePicker = styled(DatePicker)`
  height: 50px;
  border: 2px solid ${({ theme }) => theme.colors.black100};
  background: transparent;
  border-radius: 13px;
  padding: 2px 10px;
  padding-bottom: 4px;
  padding-top: 2px;
  width: 100%;

  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colors.systemBlue};
  }
`;

const FormGenerator = ({
  type,
  placeholder = "",
  isDisabled = false,
  onChange,
  className = "",
  value,
  dateFormat = null,
  readOnly = false,
  defaultValue = "",
  ...rest
}) => {
  let formField = "";

  switch (type) {
    case "text":
      formField = (
        <StyledInput
          type="text"
          readOnly={readOnly}
          placeholder={placeholder}
          disabled={isDisabled}
          onChange={onChange}
          className={(classNames("form-control"), className)}
          value={value}
          autoComplete="off"
          {...rest}
        />
      );
      break;

    case "number":
      formField = (
        <StyleNumericFormat
          className={(classNames("form-control"), className)}
          defaultValue={defaultValue}
          value={value}
          readOnly={readOnly}
          decimalSeparator="."
          thousandSeparator=","
          onValueChange={onChange}
          placeholder={placeholder}
          disabled={isDisabled}
          {...rest}
        />
      );
      break;

    case "date":
      formField = (
        <Flatpickr
          value={value}
          readOnly={readOnly}
          defaultValue={defaultValue}
          placeholder={placeholder}
          className={className}
          options={{
            enableTime: false,
            dateFormat,
          }}
          onChange={onChange}
        />
      );
      break;

    case "mobile":
      // Function to format value for display
      // eslint-disable-next-line no-case-declarations
      const formatValueForDisplay = (val) => {
        if (!val || typeof val !== "string") return "";

        // If value starts with +63, replace with 0
        if (val.startsWith("+63")) {
          return `0${val.substring(3)}`;
        }

        // If value starts with 63, replace with 0
        if (val.startsWith("63")) {
          return `0${val.substring(2)}`;
        }

        // If value already starts with 0, keep as is
        if (val.startsWith("0")) {
          return val;
        }

        // If value starts with 9, add leading 0
        if (val.startsWith("9")) {
          return `0${val}`;
        }

        return val;
      };

      formField = (
        <StyledInput
          value={formatValueForDisplay(value)}
          type="text"
          className={(classNames("form-control"), className)}
          placeholder={placeholder}
          disabled={isDisabled}
          onKeyDown={(event) => {
            const allowedKeys = [
              "Backspace",
              "Delete",
              "ArrowLeft",
              "ArrowRight",
              "Tab",
            ];

            const isCtrlOrCmd = event.ctrlKey || event.metaKey;

            const isShortcut =
              isCtrlOrCmd &&
              ["a", "c", "v", "x"].includes(event.key.toLowerCase());

            if (
              !/[0-9]/.test(event.key) &&
              !allowedKeys.includes(event.key) &&
              !isShortcut
            ) {
              event.preventDefault();
            }
          }}
          onPaste={(evt) => {
            evt.preventDefault();
            const pastedText = evt.clipboardData.getData("text");
            const cleanNumber = pastedText.replace(/\D/g, "");

            // Check if pasted number is valid format (starts with 09 or 9)
            if (cleanNumber.startsWith("09") || cleanNumber.startsWith("9")) {
              const numberToSave = cleanNumber.startsWith("0")
                ? cleanNumber.substring(1)
                : cleanNumber;
              onChange(numberToSave);
            }
          }}
          onChange={(evt) => {
            const val = evt.target.value.replace(/\D/g, "");

            // Handle empty value
            if (val === "") {
              onChange("");

              return;
            }

            if (val === "0") {
              onChange("0"); // Keep the single "0"
            } else if (val.startsWith("09")) {
              onChange(val); // Keep "09xxxxxxx" (11 digits)
            } else if (val.startsWith("9")) {
              onChange(`0${val}`); // Add leading zero "9xxxxxxx" → "09xxxxxxx"
            }
          }}
          autoComplete="off"
          maxLength={11}
          minLength={11}
          {...rest}
        />
      );
      break;

    case "textarea":
      formField = (
        <StyledTextarea
          type="textarea"
          readOnly={readOnly}
          placeholder={placeholder}
          disabled={isDisabled}
          onChange={onChange}
          className={(classNames("form-control"), className)}
          value={value}
          autoComplete="off"
          {...rest}
        />
      );
      break;

    case "datetime":
      formField = (
        <StyledDatePicker
          className={(classNames("form-control"), className)}
          onChange={onChange}
          dateFormat={dateFormat || "MMM-dd-yyyy"}
          {...rest}
        />
      );
      break;

    default:
      break;
  }

  return formField;
};

FormGenerator.propTypes = {
  type: PropTypes.string,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dateFormat: PropTypes.string,
  readOnly: PropTypes.bool,
  defaultValue: PropTypes.string,
};

export default FormGenerator;
